import { MutationTuple } from '@apollo/client';
import { shouldSkipAddUserToMode } from '@thoughtspot/session-service';
import { useEffect, useState } from 'react';

// Define the types for the functions
export type GetStringFunction = () => string;

// Define the types for the functions
export type GetNumberFunction = () => number;

export type UseAddUserToModeMutationType = (
    onCompleted: (data: any) => void,
    onError: (error: any) => void,
    variables: any,
) => MutationTuple<any, any>;

export type UseGetAnalystStudioAccessInfoLazyQueryType = () => {
    getStudioAccessInfo: (variables: { orgId: string }) => void;
    analystStudioAccessData: any;
    loading: boolean;
    error: any;
};

export interface AddUserResponse {
    status: number;
}

// Default Identity service headers type.
export interface DefaultIdentityServiceHeaders {
    accessToken: string;
    xTsPrivileges: string;
    xTsIdpDomain: string;
}

// get default identity service headers.
export const getDefaultSamlHeaders = (
    getAccessToken: GetStringFunction,
    getIdpDomain: GetStringFunction,
    getUserPrivileges: GetStringFunction,
): DefaultIdentityServiceHeaders => {
    return {
        accessToken: `Bearer ${getAccessToken()}`,
        xTsPrivileges: btoa(JSON.stringify(getUserPrivileges())),
        xTsIdpDomain: getIdpDomain(),
    };
};

// use add user to analysts studio mutation.
export const useAnalystStudioMutation = (
    getAccessToken: GetStringFunction,
    getOktaUserId: GetStringFunction,
    getUserPrivileges: GetStringFunction,
    getCurrentOrgId: GetNumberFunction,
    useAddUserToModeMutation: UseAddUserToModeMutationType,
    useGetAnalystStudioAccessInfoLazyQuery: UseGetAnalystStudioAccessInfoLazyQueryType,
    getIdpDomain: GetStringFunction,
    getClientId: GetStringFunction,
) => {
    const [isRequestPending, setRequestPending] = useState<boolean>(false);
    const [analystStudioWindow, setAnalystStudioWindow] = useState<any>();
    const [datasetId, setDatasetId] = useState<string>('');
    const [reportId, setReportId] = useState<string>('');
    const [viewId, setViewId] = useState<string>('');

    const prepareTargetUrl = (workspaceName: string) => {
        if (datasetId === null) return '';
        if (datasetId === '-1') return `/editor/${workspaceName}/datasets/new`;
        if (datasetId !== '-1' && reportId === '')
            return `/${workspaceName}/datasets/${datasetId}`;
        if (reportId !== '-1' && viewId !== '')
            return `/editor/${workspaceName}/reports/${reportId}/datasets/${viewId}`;
        return '';
    };

    // Reusable function to make either GET or POST request
    const makeAuthRequest = (
        url: string,
        authToken: string,
        targetUrl: string,
    ) => {
        let destinationUrl = url;
        const requestOptions: RequestInit = {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        };

        if (!shouldSkipAddUserToMode()) {
            requestOptions.method = 'POST';
            if (targetUrl && targetUrl.length !== 0) {
                requestOptions.body = JSON.stringify({
                    target_url: targetUrl,
                });
            }
        } else {
            requestOptions.method = 'GET';
            // Convert body to query parameter for GET request
            const urlWithParams = new URL(url);
            if (targetUrl && targetUrl.length !== 0) {
                urlWithParams.searchParams.set('target_url', targetUrl);
            }
            destinationUrl = urlWithParams.toString();
        }

        return fetch(destinationUrl, requestOptions);
    };

    // Navigate to mode window with authentication
    const doModeAuthenticationAndRedirect = (
        url: string,
        workspaceName: string,
        authToken: string,
    ) => {
        const targetUrl = prepareTargetUrl(workspaceName);

        // Make the request using the reusable function
        makeAuthRequest(url, authToken, targetUrl)
            .then(response => {
                setRequestPending(false);

                // Manually handle redirection
                if (response.status <= 204) {
                    const redirectUrl =
                        response.headers.get('location') ||
                        response.headers.get('Location');

                    if (redirectUrl) {
                        if (analystStudioWindow) {
                            analystStudioWindow.location.href = redirectUrl;
                        } else {
                            window.open(redirectUrl, '_blank');
                        }
                    }
                } else if (analystStudioWindow) {
                    analystStudioWindow.close();
                }

                return response;
            })
            .catch(error => {
                setRequestPending(false);
                if (analystStudioWindow) {
                    analystStudioWindow.close();
                }
                return error;
            });
    };

    // Lazy query for getting analyst studio access info.
    const {
        getStudioAccessInfo: getAccessInfo,
        analystStudioAccessData: accessData,
        loading: accessLoading,
        error: accessError,
    } = useGetAnalystStudioAccessInfoLazyQuery();

    // Define the second mutation.
    const [
        addUserToMode,
        {
            data: addUserResponse,
            loading: isAddUserLoading,
            error: addUserError,
        },
    ] = useAddUserToModeMutation(
        (response: any) => {
            // TODO @Deepak.Nandwani : Handle responses.
        },
        (errors: any) => {
            // TODO @Deepak.Nandwani : Handle errors.
        },
        {
            userId: getOktaUserId(),
            clientId: getClientId(),
            defaultHeaders: getDefaultSamlHeaders(
                getAccessToken,
                getIdpDomain,
                getUserPrivileges,
            ),
        },
    );

    useEffect(() => {
        if (!(accessLoading || isAddUserLoading)) {
            if (accessError || addUserError) {
                if (analystStudioWindow) {
                    analystStudioWindow.close();
                }
                setRequestPending(false);
            } else if (
                (addUserResponse || shouldSkipAddUserToMode()) &&
                accessData
            ) {
                const {
                    analystStudioUrl,
                    analystStudioWSId,
                    jwtToken,
                } = accessData;
                setRequestPending(false);
                doModeAuthenticationAndRedirect(
                    `https://${analystStudioUrl}`,
                    analystStudioWSId,
                    jwtToken,
                );
            }
        }
    }, [
        accessData,
        accessLoading,
        accessError,
        isAddUserLoading,
        addUserError,
        addUserResponse,
    ]);

    // use effect will stop subsequent requests till one is going on.
    useEffect(() => {
        if (isRequestPending) {
            // trigger all hooks, request arrived.
            if (!shouldSkipAddUserToMode()) {
                addUserToMode();
            }
            getAccessInfo({ orgId: `${getCurrentOrgId()}` });
        }
    }, [isRequestPending]);

    const onAnalystStudioClicked = (
        datasetIdTmp: string,
        reportId: string,
        viewId: string,
    ) => {
        setDatasetId(datasetIdTmp);
        setReportId(reportId);
        setViewId(viewId);

        // make a request to trigger hooks.
        if (!isRequestPending) {
            const analystStudioWindow = window.open('/#/loader', '_blank');
            setAnalystStudioWindow(analystStudioWindow);
        }
        setRequestPending(true);
    };
    return { onAnalystStudioClicked, isRequestPending };
};
